class DtcCartDrawer extends HTMLElement {
  connectedCallback() {
    this._quantityDebounceTimers = new Map();
    this.addEventListener('click', this._handleClick.bind(this));
    this.addEventListener('change', this._handleChange.bind(this));
    this.addEventListener('keyup', this._handleKeyup.bind(this));
    this.syncQuantityInputs();
  }

  syncQuantityInputs() {
    this.querySelectorAll('.dtc-cart-drawer__quantity-input').forEach((input) => {
      input.dataset.lastCommitted = String(Number(input.value) || 0);
    });
  }

  open() {
    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }

  async _handleClick(event) {
    if (event.target.closest('[data-dtc-cart-close]')) {
      this.close();
      return;
    }

    const removeLink = event.target.closest('[data-dtc-cart-remove]');
    if (removeLink) {
      event.preventDefault();
      const line = Number(removeLink.dataset.line);
      const input = this.querySelector(`[data-line="${line}"].dtc-cart-drawer__quantity-input`);
      const lineEl = removeLink.closest('.dtc-cart-drawer__item');
      const previousValue = Number(
        input?.value ?? lineEl?.dataset.quantity ?? removeLink.getAttribute('data-v-gtm-quantity') ?? 0
      );
      await window.DtcCartDrawerController.changeLine(line, 0, { previousValue });
      return;
    }

    const quantityButton = event.target.closest('[data-dtc-cart-quantity-change]');
    if (!quantityButton) return;

    event.preventDefault();

    const line = Number(quantityButton.dataset.line);
    const input = this.querySelector(`[data-line="${line}"].dtc-cart-drawer__quantity-input`);
    const currentValue = Number(input ? input.value : 0);
    const nextValue = quantityButton.dataset.dtcCartQuantityChange === 'increase'
      ? currentValue + 1
      : Math.max(currentValue - 1, 0);

    if (nextValue === currentValue) return;

    const needsFullRefresh = nextValue === 0;
    if (!needsFullRefresh && input) {
      input.value = nextValue;
    }

    await window.DtcCartDrawerController.changeLine(line, nextValue, {
      previousValue: currentValue,
      optimistic: !needsFullRefresh,
    });
  }

  _handleChange(event) {
    const input = event.target.closest('.dtc-cart-drawer__quantity-input');
    if (!input) return;

    const line = Number(input.dataset.line);
    const lineEl = input.closest('.dtc-cart-drawer__item');
    const previousValue = Number(
      input.dataset.lastCommitted ?? lineEl?.dataset.quantity ?? input.getAttribute('value') ?? 0
    );

    clearTimeout(this._quantityDebounceTimers.get(line));
    this._quantityDebounceTimers.set(
      line,
      setTimeout(async () => {
        const value = Math.max(Number(input.value), 0);
        if (value === previousValue) return;

        const needsFullRefresh = value === 0;
        if (!needsFullRefresh) {
          input.value = value;
        }

        await window.DtcCartDrawerController.changeLine(line, value, {
          previousValue,
          optimistic: !needsFullRefresh,
        });

        if (!needsFullRefresh) {
          input.dataset.lastCommitted = String(value);
        }
      }, typeof ON_CHANGE_DEBOUNCE_TIMER !== 'undefined' ? ON_CHANGE_DEBOUNCE_TIMER : 300)
    );
  }

  _handleKeyup(event) {
    if (event.code === 'Escape') {
      this.close();
    }
  }
}

if (!customElements.get('dtc-cart-drawer')) {
  customElements.define('dtc-cart-drawer', DtcCartDrawer);
}

document.addEventListener('click', (event) => {
  const checkoutBtn = event.target.closest('#DtcCartDrawer-Checkout');
  if (!checkoutBtn || checkoutBtn.disabled) return;

  const form = document.getElementById('DtcCartDrawer-Form');
  if (!form) return;

  event.preventDefault();

  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit(checkoutBtn);
    return;
  }

  form.submit();
});

if (!customElements.get('add-to-favorites')) {
  class AddToFavorites extends HTMLElement {
    connectedCallback() {
      this._button = this.querySelector('button');
      this._productJSON = this._getProductJSON();
      if (!this._button || !this._productJSON) return;

      const favorites = this._getFavorites();
      this._syncActiveState(this._button, this._productJSON, favorites);

      this._onClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this._toggleFavorite(this._productJSON, this._button);
      };

      this._button.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      if (this._button && this._onClick) {
        this._button.removeEventListener('click', this._onClick);
      }
    }

    _getProductJSON() {
      const raw = this.getAttribute('product-json');
      if (!raw) return null;

      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }

    _getFavorites() {
      try {
        const favorites = JSON.parse(localStorage.getItem('favorites'));
        return Array.isArray(favorites) ? favorites : [];
      } catch {
        return [];
      }
    }

    _getFavoriteId(productJSON) {
      return String(productJSON.id ?? '');
    }

    _isFavorite(favorites, productJSON) {
      const favoriteId = this._getFavoriteId(productJSON);
      if (!favoriteId) return false;

      return favorites.some((obj) => String(obj.id ?? '') === favoriteId);
    }

    _syncActiveState(button, productJSON, favorites) {
      if (this._isFavorite(favorites, productJSON)) {
        button.classList.add('is-active');
      }
    }

    _toggleFavorite(productJSON, button) {
      let favorites = this._getFavorites();

      const favoriteId = this._getFavoriteId(productJSON);

      if (this._isFavorite(favorites, productJSON)) {
        const nextFavorites = favorites.filter((obj) => String(obj.id ?? '') !== favoriteId);
        localStorage.setItem('favorites', JSON.stringify(nextFavorites));
        button.classList.remove('is-active');
        this._trackRemoveFromWishlist();
        return;
      }

      favorites.push(productJSON);
      localStorage.setItem('favorites', JSON.stringify(favorites));
      button.classList.add('is-active');
      this._trackAddToWishlist();
    }

    _getWishlistItemData() {
      let variantTitle = this.getAttribute('data-variant-title') || this.getAttribute('data-v-gtm-item-variant') || '';
      if (variantTitle === 'Default Title') {
        variantTitle = '';
      }

      const productId =
        this.getAttribute('data-product-id') || this.getAttribute('data-v-gtm-product-id') || '';

      return {
        item_id: this.getAttribute('data-product-sku') || this.getAttribute('data-v-gtm-item-id') || '',
        item_name: this.getAttribute('data-product-title') || this.getAttribute('data-v-gtm-item-name') || '',
        item_category: this.getAttribute('data-product-type') || this.getAttribute('data-v-gtm-item-category') || '',
        item_variant: variantTitle,
        price:
          parseFloat(this.getAttribute('data-product-price') || this.getAttribute('data-v-gtm-price')) || 0,
        quantity: parseInt(this.getAttribute('data-v-gtm-quantity'), 10) || 1,
        discount:
          parseFloat(this.getAttribute('data-discount') || this.getAttribute('data-v-gtm-discount')) || 0,
        index: parseInt(this.getAttribute('data-index') || this.getAttribute('data-v-gtm-index'), 10) || 0,
        item_list_id: this.getAttribute('data-list-id') || this.getAttribute('data-v-gtm-item-list-id') || '',
        item_list_name: this.getAttribute('data-list-name') || this.getAttribute('data-v-gtm-item-list-name') || '',
        product_id: productId,
      };
    }

    _shouldTrackWishlist() {
      return (
        this.hasAttribute('data-v-gtm-wishlist') ||
        this.hasAttribute('data-product-sku')
      );
    }

    _trackAddToWishlist() {
      if (typeof GTMTrackerGA4 === 'undefined' || !this._shouldTrackWishlist()) {
        return;
      }
      GTMTrackerGA4.addToWishlist(this._getWishlistItemData(), {
        module:
          this.getAttribute('data-list-name') ||
          this.getAttribute('data-v-gtm-module') ||
          'Favorite Page',
      });
    }

    _trackRemoveFromWishlist() {
      if (typeof GTMTrackerGA4 === 'undefined' || !this._shouldTrackWishlist()) {
        return;
      }
      GTMTrackerGA4.removeFromWishlist(this._getWishlistItemData());
    }

    trackRemoveFromWishlist() {
      this._trackRemoveFromWishlist();
    }
  }

  customElements.define('add-to-favorites', AddToFavorites);
}

/**
 * swiper-slider  - initializes Swiper from slider-config JSON (Levoit DTC pattern).
 * Requires assets/swiper.js (bundle) loaded before dtc.js.
 */
if (!customElements.get('swiper-slider')) {
  class BaseSwiper extends HTMLElement {
    constructor() {
      super();
      this.LOADED = false;
      this.LOAD_WHEN_IN_VIEW = this.hasAttribute('load-when-in-view');
      this.DISABLE_ON_BREAKPOINT = this.hasAttribute('disable-on-breakpoint');
      this.BREAKPOINT = parseInt(this.getAttribute('disable-on-breakpoint'), 10);
      this._onResize = this.handleResize.bind(this);
      this._onSectionLoad = this._handleSectionLoad.bind(this);
    }

    connectedCallback() {
      this.swiperEl = this.querySelector('.swiper');
      const configAttr = this.getAttribute('slider-config');
      if (!configAttr || !this.swiperEl) return;

      try {
        this.getConfig = JSON.parse(configAttr);
      } catch (error) {
        console.warn('swiper-slider: invalid slider-config', error);
        return;
      }

      this.swiperNav = this.querySelector('[swiper-nav]');
      this.config = this._buildConfig();

      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        document.addEventListener('shopify:section:load', this._onSectionLoad);
      }

      if (this.DISABLE_ON_BREAKPOINT) {
        this.setAttribute('disabled', '');
        this.handleResize();
        window.addEventListener('resize', this._onResize);
        return;
      }

      if (this.LOAD_WHEN_IN_VIEW) {
        this._observeWhenInView();
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
    }

    _buildConfig() {
      return {
        watchSlidesProgress: true,
        a11y: {
          enabled: true,
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
          firstSlideMessage: 'This is the first slide',
          lastSlideMessage: 'This is the last slide',
          paginationBulletMessage: 'Go to slide {{index}}',
        },
        navigation: {
          nextEl: this.querySelector('[swiper-button-next]'),
          prevEl: this.querySelector('[swiper-button-prev]'),
        },
        pagination: {
          el: this.querySelector('[swiper-pagination]'),
          type: 'fraction',
        },
        ...this.getConfig,
      };
    }

    init() {
      if (!this.swiperEl || typeof Swiper === 'undefined') return;
      if (this.swiper) {
        this.swiper.destroy(true, true);
      }
      this.swiper = new Swiper(this.swiperEl, this.config);
    }

    handleResize() {
      const windowWidth = window.innerWidth;

      if (windowWidth > this.BREAKPOINT && this.LOADED) {
        this.setAttribute('disabled', '');
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
          this.LOADED = false;
        }
        if (this.swiperNav) this.swiperNav.classList.add('hidden');
        return;
      }

      if (windowWidth <= this.BREAKPOINT && !this.LOADED) {
        this.removeAttribute('disabled');
        if (this.swiperNav) this.swiperNav.classList.remove('hidden');
        this.init();
        this.LOADED = true;
      }
    }

    _observeWhenInView() {
      const options = { rootMargin: '0px 0px -30px 0px' };
      this.intersectionObserver = new IntersectionObserver((entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.swiperEl = this.querySelector('.swiper');
        this.init();
      }, options);
      this.intersectionObserver.observe(this);
    }

    _handleSectionLoad(event) {
      if (!this.isConnected) return;

      const section = this.closest('[id^="shopify-section-"]');
      if (!section || section.id !== 'shopify-section-' + event.detail.sectionId) return;

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this.LOADED = false;
      this.swiperEl = this.querySelector('.swiper');
      this.config = this._buildConfig();

      if (this.DISABLE_ON_BREAKPOINT) {
        this.handleResize();
      } else if (this.LOAD_WHEN_IN_VIEW) {
        this._observeWhenInView();
      } else {
        this.init();
      }
    }
  }

  class SwiperSlider extends BaseSwiper {}

  customElements.define('swiper-slider', SwiperSlider);
}

/**
 * product-gallery-slider  - PDP main + thumbs gallery (Levoit theme.js pattern).
 */
if (!customElements.get('product-gallery-slider')) {
  function dtcSlideSwiperToIndex(swiper, slideIndex) {
    if (!swiper || Number.isNaN(slideIndex)) return;

    swiper.params.watchOverflow = false;
    swiper.updateSize();
    swiper.updateSlides();
    swiper.updateProgress();
    swiper.allowSlideNext = true;
    swiper.allowSlidePrev = true;

    if (swiper.params.loop && typeof swiper.slideToLoop === 'function') {
      swiper.slideToLoop(slideIndex);
      return;
    }

    if (!swiper.slideTo(slideIndex)) {
      swiper.setTransition(0);
      swiper.setTranslate(-swiper.slidesGrid[slideIndex]);
      swiper.updateActiveIndex(slideIndex);
      swiper.updateSlidesClasses();
    }
  }

  class ProductGallerySlider extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.swiperEl = this.querySelector('#ProductGallerySlider-Main');
      this.thumbsEl = this.querySelector('#ProductGallerySlider-Thumbs');
      const configAttr = this.getAttribute('slider-config');
      const thumbsConfigAttr = this.getAttribute('thumbs-config');
      if (!this.swiperEl || !this.thumbsEl || !configAttr || !thumbsConfigAttr) return;
      if (typeof Swiper === 'undefined') return;

      try {
        this.getConfig = JSON.parse(configAttr);
        this.thumbsConfig = JSON.parse(thumbsConfigAttr);
      } catch (error) {
        console.warn('product-gallery-slider: invalid config', error);
        return;
      }

      this.thumbs = new Swiper(this.thumbsEl, this.thumbsConfig);
      const slideCount = this.swiperEl.querySelectorAll('.swiper-slide').length;
      const enableLoop = Boolean(this.getConfig.loop) && slideCount > 3;

      this.swiper = new Swiper(this.swiperEl, {
        ...this.getConfig,
        thumbs: { swiper: this.thumbs },
        navigation: {
          nextEl: this.querySelector('[swiper-button-next]'),
          prevEl: this.querySelector('[swiper-button-prev]'),
        },
        keyboard: { enabled: true },
        loop: enableLoop,
        watchOverflow: false,
        on: {
          afterInit: (swiper) => {
            swiper.params.watchOverflow = false;
            swiper.updateSize();
            swiper.updateSlides();
            swiper.updateProgress();

            if (
              slideCount > 1 &&
              !swiper.allowSlideNext &&
              !swiper.allowSlidePrev
            ) {
              if (swiper.params.loop && typeof swiper.loopDestroy === 'function') {
                swiper.loopDestroy();
              }
              swiper.params.loop = false;
              swiper.allowSlideNext = true;
              swiper.allowSlidePrev = true;
              swiper.update();
            }

            this.classList.remove('dtc-gallery--initializing');
          },
        },
      });

      this._onVariantChange = (event) => {
        const media = event.detail?.featured_media || event.detail?.featured_image;
        if (!media?.id || !this.swiper) return;

        const slideEl = this.swiper.el.querySelector(`[media-id="${media.id}"]`);
        if (!slideEl) return;

        const slideIndex = parseInt(slideEl.getAttribute('media-index'), 10);
        if (Number.isNaN(slideIndex)) return;

        dtcSlideSwiperToIndex(this.swiper, slideIndex);
        dtcSlideSwiperToIndex(this.thumbs, slideIndex);
      };
      document.addEventListener('color-variant:changed', this._onVariantChange);

      const zoomGallery = document.querySelector('pdp-zoom-gallery');
      if (zoomGallery) {
        this.swiper.on('click', () => {
          const realIndex = this.swiper.realIndex;
          zoomGallery.openAt(realIndex);
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('color-variant:changed', this._onVariantChange);
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      if (this.thumbs) {
        this.thumbs.destroy(true, true);
        this.thumbs = null;
      }
      this._initialized = false;
    }
  }

  customElements.define('product-gallery-slider', ProductGallerySlider);
}

/**
 * pdp-zoom-gallery  - fullscreen zoom modal (Levoit theme.js pattern).
 */
if (!customElements.get('pdp-zoom-gallery')) {
  class PdpZoomGallery extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.IS_ZOOMED = false;

      this.closeBtn = this.querySelector('[close-button]');
      this.swiperEl = this.querySelector('.swiper');
      const configAttr = this.getAttribute('slider-config');
      if (!this.swiperEl || !configAttr || typeof Swiper === 'undefined') return;

      let userConfig;
      try {
        userConfig = JSON.parse(configAttr);
      } catch (error) {
        console.warn('pdp-zoom-gallery: invalid slider-config', error);
        return;
      }

      const scrollbarEl = this.querySelector('.swiper-scrollbar');
      this.swiper = new Swiper(this.swiperEl, {
        ...userConfig,
        scrollbar: scrollbarEl ? { el: scrollbarEl, hide: false } : undefined,
        zoom: true,
        navigation: {
          nextEl: this.querySelector('[swiper-button-next]'),
          prevEl: this.querySelector('[swiper-button-prev]'),
        },
        pagination: {
          el: this.querySelector('[swiper-pagination]'),
          type: 'fraction',
        },
        keyboard: { enabled: false },
        on: {
          click: () => {
            if (!this.swiper?.zoom) return;
            if (this.IS_ZOOMED) {
              this.swiper.zoom.out();
              this.IS_ZOOMED = false;
            } else {
              this.swiper.zoom.in();
              this.IS_ZOOMED = true;
            }
          },
        },
      });

      if (this.closeBtn) {
        this._onCloseClick = () => this.close();
        this.closeBtn.addEventListener('click', this._onCloseClick);
      }

      this._onKeydown = (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-active')) this.close();
      };
      document.addEventListener('keydown', this._onKeydown);
    }

    disconnectedCallback() {
      if (this.closeBtn && this._onCloseClick) {
        this.closeBtn.removeEventListener('click', this._onCloseClick);
      }
      document.removeEventListener('keydown', this._onKeydown);
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this._initialized = false;
    }

    openAt(index) {
      if (!this.swiper) return;
      this.show();
      this.swiper.update();
      this.swiper.slideToLoop(index, 0);
    }

    show() {
      this.classList.add('is-active');
      document.body.classList.add('overflow-hidden');
      if (this.swiper?.keyboard) this.swiper.keyboard.enable();
    }

    close() {
      this.IS_ZOOMED = false;
      if (this.swiper?.zoom) this.swiper.zoom.out();
      if (this.swiper?.keyboard) this.swiper.keyboard.disable();
      this.classList.remove('is-active');
      document.body.classList.remove('overflow-hidden');
    }
  }

  customElements.define('pdp-zoom-gallery', PdpZoomGallery);
}


window.DtcCartDrawerController = {
  sectionName: 'dtc-cart-drawer',
  _changeLineQueue: Promise.resolve(),
  _linePending: new Map(),
  _lineFlushTimer: null,
  _flushRunning: false,
  _lineFlushDelay: 32,
  _loadingCount: 0,

  setDrawerLoading(busy) {
    const drawer = document.querySelector('dtc-cart-drawer');
    if (!drawer) return;

    if (busy) {
      this._loadingCount += 1;
    } else {
      this._loadingCount = Math.max(0, this._loadingCount - 1);
    }

    const isLoading = this._loadingCount > 0;
    drawer.classList.toggle('is-loading', isLoading);

    const loading = drawer.querySelector('.dtc-cart-drawer__loading');
    if (loading) loading.setAttribute('aria-hidden', isLoading ? 'false' : 'true');

    const main = drawer.querySelector('.dtc-cart-drawer__main');
    if (main) main.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  },

  async add(variantId, quantity = 1, options = {}) {
    const drawer = document.querySelector('dtc-cart-drawer');
    if (drawer) drawer.setAttribute('data-v-gtm-suppress-view-cart', '1');
    this.open();
    this.setDrawerLoading(true);

    try {
      const payload = {
        id: variantId,
        quantity,
        sections: [this.sectionName],
        sections_url: window.location.pathname,
      };
      const sellingPlan = String(options.selling_plan || options.sellingPlan || '').trim();
      if (sellingPlan) payload.selling_plan = sellingPlan;

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Unable to add this item to the cart.');
      }

      const data = await response.json();
      await this.refresh(data.sections);
      await this.syncCartBubble();
      return data;
    } finally {
      this.setDrawerLoading(false);
    }
  },

  changeLine(line, quantity, options = {}) {
    this._linePending.set(line, { quantity, options });

    clearTimeout(this._lineFlushTimer);
    this._lineFlushTimer = setTimeout(() => {
      this._lineFlushTimer = null;
      this._flushLineUpdates();
    }, this._lineFlushDelay);

    return this._changeLineQueue;
  },

  _flushLineUpdates() {
    this._changeLineQueue = this._changeLineQueue.catch(() => {}).then(() => this._runLineFlush());
    return this._changeLineQueue;
  },

  async _runLineFlush() {
    if (this._flushRunning) return;

    this._flushRunning = true;

    try {
      while (this._linePending.size > 0) {
        const entries = [...this._linePending.entries()];
        this._linePending.clear();

        for (const [line, payload] of entries) {
          await this._changeLineImpl(line, payload.quantity, payload.options);
        }
      }
    } finally {
      this._flushRunning = false;

      if (this._linePending.size > 0) {
        clearTimeout(this._lineFlushTimer);
        this._lineFlushTimer = setTimeout(() => {
          this._lineFlushTimer = null;
          this._flushLineUpdates();
        }, this._lineFlushDelay);
      }
    }
  },

  async _changeLineImpl(line, quantity, { previousValue, optimistic = false } = {}) {
    const needsSectionRender = quantity === 0;

    if (needsSectionRender) {
      this.setDrawerLoading(true);
    }

    const body = { line, quantity };
    if (needsSectionRender) {
      body.sections = [this.sectionName];
      body.sections_url = window.location.pathname;
    }

    let data;
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      data = await response.json();

      if (!response.ok) {
        this._rollbackQuantity(line, previousValue, optimistic);
        this.showError(data.description || 'Unable to update the cart.');
        return;
      }

      if (
        typeof window.vGtm !== 'undefined' &&
        window.vGtm.cart &&
        typeof window.vGtm.cart.trackLineChange === 'function' &&
        previousValue !== undefined
      ) {
        const lineEl = document.querySelector(
          `#DtcCartDrawer-Item-${line}, #CartItem-${line}, [data-line="${line}"].dtc-cart-drawer__item, [data-line="${line}"].cart-item`
        );
        const cartModule = document.getElementById('main-cart-items') ? 'Cart Page' : 'Cart Popup';
        window.vGtm.cart.trackLineChange(lineEl, previousValue, quantity, cartModule);
      }

      if (needsSectionRender || data.item_count === 0) {
        await this.refresh(data.sections || (await this._fetchSections()));
        return;
      }

      this.syncCartState(data, line);
      this.updateCartBubble(data);
      this._reconcileLineQuantity(line, data);
      this.clearError();
    } catch {
      this._rollbackQuantity(line, previousValue, optimistic);
      this.showError('Unable to update the cart.');
    } finally {
      if (needsSectionRender) {
        this.setDrawerLoading(false);
      }
    }
  },

  _reconcileLineQuantity(line, cart) {
    if (this._linePending.has(line)) return;

    const item = cart.items[line - 1];
    const input = document.querySelector(`[data-line="${line}"].dtc-cart-drawer__quantity-input`);
    if (!item || !input) return;

    if (Number(input.value) !== item.quantity) {
      input.value = item.quantity;
    }

    input.dataset.lastCommitted = String(item.quantity);
  },

  _rollbackQuantity(line, previousValue, optimistic) {
    if (!optimistic || previousValue === undefined || this._linePending.has(line)) return;

    const input = document.querySelector(`[data-line="${line}"].dtc-cart-drawer__quantity-input`);
    if (input) input.value = previousValue;
  },

  formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';

    try {
      let formatted = new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
      if (amount % 1 === 0) {
        formatted = formatted.replace(/([.,]00)(?!\d)/, '');
      }
      return formatted;
    } catch {
      const value = amount % 1 === 0 ? String(amount) : amount.toFixed(2);
      return `$${value}`;
    }
  },

  syncCartState(cart, line) {
    const drawer = document.querySelector('dtc-cart-drawer');
    if (!drawer) return;

    const subtotal = drawer.querySelector('.dtc-cart-drawer__subtotal-value');
    if (subtotal) subtotal.textContent = this.formatMoney(cart.total_price);

    const count = drawer.querySelector('.dtc-cart-drawer__count');
    if (count) {
      const itemCount = cart.item_count;
      count.textContent = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
    }

    const checkout = drawer.querySelector('#DtcCartDrawer-Checkout');
    if (checkout) checkout.disabled = cart.item_count === 0;

    const item = cart.items[line - 1];
    if (!item) return;

    const lineEl = document.getElementById(`DtcCartDrawer-Item-${line}`);
    if (!lineEl) return;

    lineEl.dataset.quantity = String(item.quantity);

    this._updateLinePrice(lineEl, item);
  },

  _updateLinePrice(lineEl, item) {
    const priceWrap = lineEl.querySelector('.dtc-cart-drawer__price');
    if (!priceWrap) return;

    if (item.original_price !== item.final_price) {
      const oldPrice = priceWrap.querySelector('.dtc-cart-drawer__old-price');
      const finalPrice = priceWrap.querySelector('.cart-item__final-price');
      if (oldPrice && finalPrice) {
        oldPrice.textContent = this.formatMoney(item.original_price);
        finalPrice.textContent = this.formatMoney(item.final_price);
        return;
      }

      priceWrap.innerHTML = `<span class="sr-only">Regular price</span><s class="cart-item__old-price dtc-cart-drawer__old-price">${this.formatMoney(item.original_price)}</s><span class="sr-only">Sale price</span><span class="cart-item__final-price">${this.formatMoney(item.final_price)}</span>`;
      return;
    }

    priceWrap.textContent = this.formatMoney(item.original_price);
  },

  updateCartBubble(cart) {
    const bubble = document.querySelector('.dtc-header #cart-icon-bubble');
    if (!bubble) return;

    const countWrap = bubble.querySelector('.dtc-header__cart-count');
    if (!countWrap) return;

    countWrap.classList.toggle('dtc-header__cart-count--empty', cart.item_count === 0);
    countWrap.classList.toggle('dtc-header__cart-count--wide', cart.item_count >= 100);

    let countSpan = countWrap.querySelector('[aria-hidden="true"]');
    if (cart.item_count > 0) {
      if (!countSpan) {
        countSpan = document.createElement('span');
        countSpan.setAttribute('aria-hidden', 'true');
        countWrap.appendChild(countSpan);
      }
      countSpan.textContent = cart.item_count < 100 ? String(cart.item_count) : '99+';
      return;
    }

    if (countSpan) countSpan.remove();
  },

  async syncCartBubble() {
    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      this.updateCartBubble(cart);
    } catch {
      // Ignore bubble sync failures; drawer state remains authoritative.
    }
  },

  async _fetchSections() {
    const response = await fetch(`${window.location.pathname}?sections=${this.sectionName}`);
    return response.json();
  },

  clearError() {
    const errors = document.getElementById('DtcCartDrawer-CartErrors');
    if (errors) errors.textContent = '';
  },

  refreshDrawerContents(nextDrawer, currentDrawer) {
    const wasActive = currentDrawer.classList.contains('active');

    ['.dtc-cart-drawer__items', '.dtc-cart-drawer__footer'].forEach((selector) => {
      const target = currentDrawer.querySelector(selector);
      const source = nextDrawer.querySelector(selector);
      if (target && source) {
        target.replaceWith(source);
      }
    });

    currentDrawer.classList.toggle('is-empty', nextDrawer.classList.contains('is-empty'));

    const main = currentDrawer.querySelector('.dtc-cart-drawer__main');
    const nextMain = nextDrawer.querySelector('.dtc-cart-drawer__main');
    if (main && nextMain) {
      main.classList.toggle('is-empty', nextMain.classList.contains('is-empty'));
    }

    if (wasActive) {
      currentDrawer.classList.add('active');
      document.body.classList.add('overflow-hidden');
    }

    if (typeof currentDrawer.syncQuantityInputs === 'function') {
      currentDrawer.syncQuantityInputs();
    }
  },

  async refresh(sections) {
    let sectionHtml = sections && sections[this.sectionName];

    if (!sectionHtml) {
      const response = await fetch(`${window.location.pathname}?sections=${this.sectionName}`);
      const data = await response.json();
      sectionHtml = data[this.sectionName];
    }

    if (!sectionHtml) return;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const nextDrawer = doc.querySelector('dtc-cart-drawer');
    const currentDrawer = document.querySelector('dtc-cart-drawer');

    if (nextDrawer && currentDrawer) {
      this.refreshDrawerContents(nextDrawer, currentDrawer);
    }

    await this.syncCartBubble();
  },

  open() {
    const drawer = document.querySelector('dtc-cart-drawer');
    if (drawer) drawer.open();
  },

  showError(message) {
    const errors = document.getElementById('DtcCartDrawer-CartErrors');
    if (errors) errors.textContent = message;
  },

  getVariantId(trigger) {
    const form =
      trigger.closest('[data-dtc-cart-drawer-form]') ||
      trigger.closest('form') ||
      (trigger.getAttribute && trigger.getAttribute('form')
        ? document.getElementById(trigger.getAttribute('form'))
        : null);

    if (form) {
      const variantInput = form.querySelector('select[name="id"], input[name="id"]');
      if (variantInput?.value) return variantInput.value;
    }

    if (trigger.dataset?.variantId) return trigger.dataset.variantId;
    if (form?.dataset?.variantId) return form.dataset.variantId;

    return null;
  },

  getQuantity(trigger) {
    if (trigger.dataset.quantity) return Math.max(Number(trigger.dataset.quantity), 1);

    const form = trigger.closest('form');
    const quantityInput = form
      ? form.elements.namedItem('quantity') || form.querySelector('[name="quantity"]')
      : null;
    const quantity = quantityInput ? Number(quantityInput.value) : 1;

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  },

  _sellingPlanFieldValue(field) {
    if (!field) return '';

    let value = '';

    if (typeof RadioNodeList !== 'undefined' && field instanceof RadioNodeList) {
      value = String(field.value || '').trim();
    } else if (typeof field.length === 'number' && field.tagName !== 'SELECT' && field[0]) {
      const checked = Array.from(field).find((el) => el.checked && String(el.value || '').trim());
      value = checked ? String(checked.value).trim() : '';
    } else if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) {
      value = '';
    } else {
      value = String(field.value || '').trim();
    }

    if (!value || value === '0' || value === 'null' || value === 'undefined') return '';
    return value;
  },

  getSellingPlanId(trigger) {
    if (trigger.dataset.sellingPlan) return String(trigger.dataset.sellingPlan).trim();

    const form = trigger.closest('form');
    const root =
      trigger.closest('.main-dtc-product__form-inner') ||
      trigger.closest('.main-product__aside') ||
      trigger.closest('product-form') ||
      form ||
      trigger;

    const checked = root.querySelector('[name="selling_plan"]:checked');
    const checkedValue = this._sellingPlanFieldValue(checked);
    if (checkedValue) return checkedValue;

    if (form && form.elements) {
      const namedValue = this._sellingPlanFieldValue(form.elements.namedItem('selling_plan'));
      if (namedValue) return namedValue;
    }

    const loose = root.querySelector('[name="selling_plan"]');
    return this._sellingPlanFieldValue(loose);
  },

  resolveAddToCartGtm(trigger) {
    const form = trigger.closest('[data-dtc-cart-drawer-form]') || trigger.closest('form');
    const listRoot = trigger.closest('[data-v-gtm-item-list]');
    const module =
      readGtmAttr(trigger, 'module') ||
      readGtmAttr(form, 'module') ||
      (form && form.getAttribute('data-v-gtm-module')) ||
      readGtmAttr(listRoot, 'module') ||
      (trigger.closest('[data-v-gtm-module]') &&
        readGtmAttr(trigger.closest('[data-v-gtm-module]'), 'module')) ||
      'Product Details Page';

    const buttonName =
      readGtmAttr(trigger, 'button_name') ||
      (
        trigger.querySelector(
          '.dtc-product-form__atc-btn-text, .dtc-product-card__atc-btn-text, .product-form__submit > span'
        ) || {}
      ).textContent ||
      trigger.textContent ||
      'Add to Cart';

    return {
      module: module,
      button_name: String(buttonName).trim().replace(/\s+/g, ' '),
      item_list_id:
        readGtmAttr(trigger, 'item-list-id') ||
        readGtmAttr(listRoot, 'item-list-id') ||
        module,
      item_list_name:
        readGtmAttr(trigger, 'item-list-name') ||
        readGtmAttr(listRoot, 'item-list-name') ||
        module,
      index: parseInt(readGtmAttr(trigger, 'index'), 10) || 0,
    };
  },
};

function enhanceDtcCartIcon(scope = document) {
  const cartLink = scope.querySelector('.dtc-header #cart-icon-bubble');
  if (!cartLink || cartLink.dataset.dtcCartEnhanced === 'true') return;

  cartLink.dataset.dtcCartEnhanced = 'true';
  cartLink.setAttribute('role', 'button');
  cartLink.setAttribute('aria-haspopup', 'dialog');
}

function openDtcCartFromIcon(event) {
  const cartLink = event.target.closest('#cart-icon-bubble');
  if (!cartLink || !cartLink.closest('.dtc-header')) return;
  if (!document.querySelector('dtc-cart-drawer')) return;

  event.preventDefault();
  window.DtcCartDrawerController.open();
}

document.addEventListener('click', openDtcCartFromIcon, true);
document.addEventListener('keydown', (event) => {
  const cartLink = event.target.closest('#cart-icon-bubble');
  if (!cartLink || !cartLink.closest('.dtc-header')) return;
  if (!document.querySelector('dtc-cart-drawer')) return;

  if (event.code.toUpperCase() === 'SPACE' || event.code === 'Enter') {
    event.preventDefault();
    window.DtcCartDrawerController.open();
  }
});

enhanceDtcCartIcon();

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = document.getElementById('shopify-section-' + event.detail.sectionId);
    if (section) enhanceDtcCartIcon(section);
  });
}

async function handleDtcAddToCart(trigger) {
  if (trigger.disabled || trigger.classList.contains('is-loading')) return;

  const variantId = window.DtcCartDrawerController.getVariantId(trigger);
  if (!variantId) {
    window.DtcCartDrawerController.showError('Please select a product option before adding to cart.');
    window.DtcCartDrawerController.open();
    return;
  }

  trigger.classList.add('is-loading');
  trigger.setAttribute('aria-busy', 'true');

  const quantity = window.DtcCartDrawerController.getQuantity(trigger);
  const sellingPlan = window.DtcCartDrawerController.getSellingPlanId(trigger);

  try {
    const lineItem = await window.DtcCartDrawerController.add(variantId, quantity, {
      selling_plan: sellingPlan,
    });
    if (
      lineItem &&
      window.vGtm &&
      window.vGtm.cart &&
      typeof window.vGtm.cart.trackAdd === 'function'
    ) {
      window.vGtm.cart.trackAdd(lineItem, quantity, Object.assign(resolveDtcAddToCartGtm(trigger), { trigger: trigger }));
    }
  } catch (error) {
    window.DtcCartDrawerController.showError(error.message);
    window.DtcCartDrawerController.open();
  } finally {
    trigger.classList.remove('is-loading');
    trigger.removeAttribute('aria-busy');
  }
}

function readGtmAttr(el, key) {
  if (!el || !el.getAttribute) return '';
  return (el.getAttribute('data-v-gtm-' + key) || '').trim();
}

function resolveDtcAddToCartGtm(trigger) {
  return window.DtcCartDrawerController.resolveAddToCartGtm(trigger);
}

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-dtc-cart-drawer-form]');
  if (!form) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const trigger = event.submitter || form.querySelector('[data-dtc-add-to-cart], [type="submit"]') || form;
  await handleDtcAddToCart(trigger);
}, true);

document.addEventListener('click', async (event) => {
  const trigger = event.target.closest('[data-dtc-add-to-cart]');
  if (!trigger) return;

  event.preventDefault();
  event.stopPropagation();

  await handleDtcAddToCart(trigger);
});

(() => {
  /**
   * DTC product form  - description line-clamp + expand toggle (Figma 10269:44385)
   */
  function initDtcProductDescriptions(scope) {
    const root = scope || document;
    root.querySelectorAll("[data-dtc-product-description]").forEach((wrap) => {
      if (wrap.dataset.dtcDescriptionInit === "true") return;
      wrap.dataset.dtcDescriptionInit = "true";

      const content = wrap.querySelector(".dtc-product-form__description");
      const toggle = wrap.querySelector(".dtc-product-form__description-toggle");
      if (!content || !toggle) return;

      const labelMore = toggle.dataset.labelMore || "Show more";
      const labelLess = toggle.dataset.labelLess || "Show less";

      function measureOverflow() {
        content.classList.add("is-clamped");
        return content.scrollHeight > content.clientHeight + 1;
      }

      function updateToggleVisibility() {
        if (wrap.dataset.expanded === "true") return;

        const overflows = measureOverflow();
        toggle.hidden = !overflows;

        if (!overflows) {
          content.classList.remove("is-clamped");
          toggle.setAttribute("aria-expanded", "false");
          syncToggleChrome(false, { settled: false, showEllipsis: false });
        } else if (wrap.dataset.expanded !== "true") {
          syncToggleChrome(false, { settled: false, showEllipsis: true });
        }
      }

      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      const ELLIPSIS_REVEAL_DELAY_MS = 140;
      let descriptionHeightTransitionCleanup = null;
      let ellipsisRevealTimer = null;

      function clearDescriptionHeight() {
        content.style.maxHeight = "";
        content.classList.remove("is-collapsing");
      }

      function setEllipsisVisible(visible, delayMs = 0) {
        clearTimeout(ellipsisRevealTimer);
        if (!visible) {
          toggle.classList.remove("is-ellipsis-visible");
          return;
        }
        if (delayMs <= 0) {
          toggle.classList.add("is-ellipsis-visible");
          return;
        }
        ellipsisRevealTimer = setTimeout(() => {
          if (wrap.dataset.expanded !== "true") {
            toggle.classList.add("is-ellipsis-visible");
          }
        }, delayMs);
      }

      function syncToggleChrome(expanded, { settled = expanded, showEllipsis = !expanded } = {}) {
        toggle.classList.toggle("is-expanded", expanded);
        toggle.classList.toggle("is-toggle-settled", settled);
        if (showEllipsis) {
          setEllipsisVisible(true, 0);
        } else {
          setEllipsisVisible(false);
        }
      }

      function cleanupDescriptionHeightTransition(options = {}) {
        const { revealEllipsis = false } = options;
        clearTimeout(ellipsisRevealTimer);
        if (!descriptionHeightTransitionCleanup) return;
        descriptionHeightTransitionCleanup();
        descriptionHeightTransitionCleanup = null;
        const expanded = wrap.dataset.expanded === "true";
        content.classList.toggle("is-clamped", !expanded);
        clearDescriptionHeight();
        syncToggleChrome(expanded, { showEllipsis: false });
        if (revealEllipsis && !expanded) {
          setEllipsisVisible(true, ELLIPSIS_REVEAL_DELAY_MS);
        }
      }

      function getClampHeight() {
        const wasClamped = content.classList.contains("is-clamped");
        content.classList.add("is-clamped");
        clearDescriptionHeight();
        const height = content.clientHeight;
        if (!wasClamped) content.classList.remove("is-clamped");
        return height;
      }

      function animateDescriptionHeight(expanded) {
        cleanupDescriptionHeightTransition();

        if (prefersReducedMotion) {
          content.classList.toggle("is-clamped", !expanded);
          clearDescriptionHeight();
          syncToggleChrome(expanded);
          return;
        }

        if (expanded) {
          setEllipsisVisible(false);
          toggle.classList.add("is-expanded");
          toggle.classList.remove("is-toggle-settled");

          const clampHeight = getClampHeight();
          content.classList.remove("is-clamped", "is-collapsing");
          const fullHeight = content.scrollHeight;

          content.style.maxHeight = clampHeight + "px";
          content.offsetHeight;
          content.style.maxHeight = fullHeight + "px";

          const onExpandEnd = (event) => {
            if (event.target !== content || event.propertyName !== "max-height") {
              return;
            }
            cleanupDescriptionHeightTransition();
          };
          content.addEventListener("transitionend", onExpandEnd);
          descriptionHeightTransitionCleanup = () => {
            content.removeEventListener("transitionend", onExpandEnd);
            clearDescriptionHeight();
          };
          return;
        }

        setEllipsisVisible(false);
        toggle.classList.remove("is-expanded", "is-toggle-settled");

        const clampHeight = getClampHeight();
        const fullHeight = content.scrollHeight;

        if (fullHeight <= clampHeight + 1) {
          content.classList.add("is-clamped");
          clearDescriptionHeight();
          syncToggleChrome(false, { showEllipsis: true });
          return;
        }

        content.classList.remove("is-clamped", "is-collapsing");
        content.style.maxHeight = fullHeight + "px";
        content.offsetHeight;
        content.classList.add("is-collapsing");
        content.style.maxHeight = clampHeight + "px";

        const onCollapseEnd = (event) => {
          if (event.target !== content || event.propertyName !== "max-height") {
            return;
          }
          cleanupDescriptionHeightTransition({ revealEllipsis: true });
        };
        content.addEventListener("transitionend", onCollapseEnd);
        descriptionHeightTransitionCleanup = () => {
          content.removeEventListener("transitionend", onCollapseEnd);
          clearDescriptionHeight();
        };
      }

      function setExpanded(expanded, options = {}) {
        const { animate = true } = options;
        wrap.dataset.expanded = expanded ? "true" : "false";
        toggle.setAttribute("aria-expanded", String(expanded));
        toggle.setAttribute("aria-label", expanded ? labelLess : labelMore);

        if (animate && !prefersReducedMotion) {
          animateDescriptionHeight(expanded);
        } else {
          content.classList.toggle("is-clamped", !expanded);
          clearDescriptionHeight();
          syncToggleChrome(expanded);
        }
      }

      setExpanded(false, { animate: false });
      updateToggleVisibility();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          wrap.classList.add("is-description-chrome-ready");
        });
      });

      let resizeTimer;
      window.addEventListener("resize", () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(updateToggleVisibility, 100);
      });

      toggle.addEventListener("click", () => {
        setExpanded(wrap.dataset.expanded !== "true", { animate: true });
      });
    });
  }

  /**
   * DTC product form  - metafield accordions expand/collapse animation
   */
  function initDtcProductAccordions(scope) {
    const root = scope || document;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    root.querySelectorAll("[data-dtc-product-accordions]").forEach((wrapper) => {
      if (wrapper.dataset.dtcAccordionsInit === "true") return;
      wrapper.dataset.dtcAccordionsInit = "true";

      wrapper.querySelectorAll("details").forEach((details) => {
        const summary = details.querySelector("summary");
        const panel = details.querySelector("[data-dtc-accordion-panel]");
        if (!summary || !panel) return;

        let transitionCleanup = null;

        function cleanupTransition() {
          if (!transitionCleanup) return;
          transitionCleanup();
          transitionCleanup = null;
        }

        function setOpenState(isOpen, { animate = true } = {}) {
          cleanupTransition();

          if (isOpen) {
            details.setAttribute("open", "");
            panel.classList.add("is-open");
            return;
          }

          details.removeAttribute("open");
          panel.classList.remove("is-open");
        }

        function waitForPanelTransition(onComplete) {
          let completed = false;

          const finish = () => {
            if (completed) return;
            completed = true;
            cleanupTransition();
            onComplete();
          };

          const onTransitionEnd = (event) => {
            if (event.target !== panel) return;
            if (
              event.propertyName !== "grid-template-rows" &&
              event.propertyName !== "opacity"
            ) {
              return;
            }
            finish();
          };

          panel.addEventListener("transitionend", onTransitionEnd);
          transitionCleanup = () => {
            panel.removeEventListener("transitionend", onTransitionEnd);
            clearTimeout(fallbackTimer);
          };

          const fallbackTimer = setTimeout(finish, 450);
        }

        if (details.hasAttribute("open")) {
          panel.classList.add("is-open");
        }

        summary.addEventListener("click", (event) => {
          event.preventDefault();

          const willOpen = !details.hasAttribute("open");

          if (prefersReducedMotion) {
            setOpenState(willOpen, { animate: false });
            return;
          }

          if (willOpen) {
            details.setAttribute("open", "");
            panel.classList.remove("is-open");
            panel.offsetHeight;
            panel.classList.add("is-open");
            return;
          }

          panel.classList.remove("is-open");
          waitForPanelTransition(() => {
            details.removeAttribute("open");
          });
        });
      });
    });
  }

  /**
   * DTC PDP variant picker — sync hidden variant id, gallery, price, and ATC state.
   */
  function initDtcVariantSync(scope) {
    const root = scope || document;
    root
      .querySelectorAll('.main-dtc-product variant-radios, variant-radios.dtc-variant-picker__radios')
      .forEach((variantRadios) => {
        if (variantRadios.dataset.dtcVariantSyncInit === 'true') return;
        variantRadios.dataset.dtcVariantSyncInit = 'true';

        const sectionId = variantRadios.dataset.section;
        if (!sectionId) return;

        const variantsScript = variantRadios.querySelector('script[type="application/json"]');
        if (!variantsScript) return;

        let variants;
        try {
          variants = JSON.parse(variantsScript.textContent);
        } catch {
          return;
        }

        const productForm = document.getElementById(`product-form-${sectionId}`);
        const variantInput = productForm?.querySelector('input[name="id"], select[name="id"]');

        if (variantInput) variantInput.disabled = false;

        function getSelectedVariant() {
          const options = Array.from(variantRadios.querySelectorAll('fieldset')).map((fieldset) => {
            const checked = fieldset.querySelector('input[type="radio"]:checked');
            return checked ? checked.value : null;
          });

          if (options.includes(null)) return null;

          return variants.find((variant) =>
            variant.options.every((option, index) => options[index] === option)
          );
        }

        function updateBuyButton(variant) {
          const atcBtn = productForm?.querySelector('[name="add"]');
          const atcText = atcBtn?.querySelector('.dtc-product-form__atc-btn-text');
          if (!atcBtn || !atcText) return;

          const soldOutLabel = window.variantStrings?.soldOut || 'Sold out';
          const addLabel =
            atcBtn.dataset.defaultLabel || window.variantStrings?.addToCart || atcText.textContent;

          if (variant?.available) {
            atcBtn.removeAttribute('disabled');
            atcText.textContent = addLabel;
          } else if (variant) {
            atcBtn.setAttribute('disabled', '');
            atcText.textContent = soldOutLabel;
          }
        }

        function updateGtmAttrs(variant) {
          if (!variant || !productForm) return;

          const atcBtn = productForm.querySelector('[data-dtc-add-to-cart]');
          const expressCheckoutEl = productForm.querySelector('.dtc-product-form__dynamic-checkout');
          const variantGtmTargets = [atcBtn, expressCheckoutEl].filter(Boolean);

          variantGtmTargets.forEach(function (target) {
            target.setAttribute('data-v-gtm-item-variant', variant.title || '');
            target.setAttribute('data-v-gtm-item-id', variant.sku || '');
            target.setAttribute('data-v-gtm-variant-id', String(variant.id || ''));
            if (variant.price != null) {
              target.setAttribute('data-v-gtm-price', String((variant.price || 0) / 100));
            }

            const compareAt = variant.compare_at_price;
            const discount =
              compareAt && compareAt > variant.price ? String((compareAt - variant.price) / 100) : '0';
            target.setAttribute('data-v-gtm-discount', discount);
          });

          productForm.setAttribute('data-variant-id', String(variant.id));
          productForm.setAttribute('data-v-gtm-variant-id', String(variant.id || ''));
          productForm.setAttribute('data-variant-title', variant.title || '');
          productForm.setAttribute('data-variant-price', String(variant.price || 0));
          if (variant.sku) productForm.setAttribute('data-product-sku', variant.sku);
        }

        function dispatchGalleryEvent(variant) {
          const media = variant?.featured_media || variant?.featured_image;
          if (!media?.id) return;

          const eventTarget = variantRadios.querySelector('fieldset[color-variant]') || variantRadios;
          eventTarget.dispatchEvent(
            new CustomEvent('color-variant:changed', {
              bubbles: true,
              detail: Object.assign({}, variant, {
                featured_media: variant.featured_media || { id: media.id },
              }),
            })
          );
        }

        async function updatePrice(variant) {
          if (!variant?.id || !variantRadios.dataset.url) return;

          const url = `${variantRadios.dataset.url}?variant=${variant.id}&section_id=${sectionId}`;
          try {
            const response = await fetch(url);
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const destination = document.getElementById(`price-${sectionId}`);
            const source = html.getElementById(`price-${sectionId}`);
            if (destination && source) destination.innerHTML = source.innerHTML;
          } catch {
            // Ignore price refresh failures; variant id sync remains authoritative.
          }
        }

        function syncVariantState() {
          const variant = getSelectedVariant();
          if (!variant) return;

          if (variantInput) {
            variantInput.disabled = false;
            variantInput.value = String(variant.id);
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          variantRadios.querySelectorAll('fieldset').forEach((fieldset) => {
            const checked = fieldset.querySelector('input[type="radio"]:checked');
            const valueEl = fieldset.querySelector('[data-selected-swatch-value]');
            if (checked && valueEl) valueEl.textContent = checked.value;
          });

          updateBuyButton(variant);
          updateGtmAttrs(variant);
          dispatchGalleryEvent(variant);
          updatePrice(variant);

          if (variantRadios.dataset.updateUrl === 'true') {
            window.history.replaceState({}, '', `${variantRadios.dataset.url}?variant=${variant.id}`);
          }
        }

        variantRadios.addEventListener('change', syncVariantState);
        syncVariantState();
      });
  }

  /**
   * DTC buy buttons  - show Amazon CTA below Sold out ATC when variant is unavailable
   */
  function initDtcSoldOutAmazon(scope) {
    const root = scope || document;
    root.querySelectorAll("[data-dtc-sold-out-actions]").forEach((soldOutActions) => {
      if (soldOutActions.dataset.dtcSoldOutAmazonInit === "true") return;
      soldOutActions.dataset.dtcSoldOutAmazonInit = "true";

      const section =
        soldOutActions.closest(".shopify-section") ||
        soldOutActions.closest("section");
      if (!section) return;

      const variantRadios = section.querySelector("variant-radios");
      if (!variantRadios) return;

      const variantsScript = variantRadios.querySelector(
        'script[type="application/json"]'
      );
      if (!variantsScript) return;

      let variants;
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch {
        return;
      }

      function getSelectedVariant() {
        const fieldsets = Array.from(variantRadios.querySelectorAll("fieldset"));
        const options = fieldsets.map((fieldset) => {
          const checked = Array.from(fieldset.querySelectorAll("input")).find(
            (input) => input.checked
          );
          return checked ? checked.value : null;
        });

        if (options.includes(null)) return null;

        return variants.find(
          (variant) =>
            !variant.options
              .map((option, index) => options[index] === option)
              .includes(false)
        );
      }

      function updateSoldOutActions() {
        const variant = getSelectedVariant();
        const isSoldOut = Boolean(variant && variant.available === false);
        soldOutActions.hidden = !isSoldOut;
      }

      variantRadios.addEventListener("change", updateSoldOutActions);
      updateSoldOutActions();
    });
  }

  /**
   * DTC PDP gallery  - thumbs vertical nav arrows
   */
  function initDtcThumbsNav(scope) {
    const root = scope || document;
    root.querySelectorAll("product-gallery-slider").forEach((gallery) => {
      if (gallery.dataset.dtcThumbsNavInit === "true") return;
      gallery.dataset.dtcThumbsNavInit = "true";

      const prevBtn = gallery.querySelector("[thumbs-button-prev]");
      const nextBtn = gallery.querySelector("[thumbs-button-next]");
      if (!prevBtn || !nextBtn) return;

      function waitForThumbs() {
        if (gallery.thumbs && gallery.thumbs.initialized) {
          bindNav(gallery.thumbs);
        } else {
          requestAnimationFrame(waitForThumbs);
        }
      }

      function bindNav(thumbsSwiper) {
        function updateBtnState() {
          prevBtn.classList.toggle("swiper-button-disabled", thumbsSwiper.isBeginning);
          prevBtn.disabled = thumbsSwiper.isBeginning;
          nextBtn.classList.toggle("swiper-button-disabled", thumbsSwiper.isEnd);
          nextBtn.disabled = thumbsSwiper.isEnd;
        }

        prevBtn.addEventListener("click", () => {
          const current = thumbsSwiper.activeIndex;
          thumbsSwiper.slideTo(Math.max(0, current - 1));
        });

        nextBtn.addEventListener("click", () => {
          thumbsSwiper.slideTo(thumbsSwiper.activeIndex + 1);
        });

        thumbsSwiper.on("slideChange", updateBtnState);
        thumbsSwiper.on("reachBeginning", updateBtnState);
        thumbsSwiper.on("reachEnd", updateBtnState);
        thumbsSwiper.on("fromEdge", updateBtnState);
        updateBtnState();
      }

      waitForThumbs();
    });
  }

  /**
   * dtc-video upload branch  - video-media + play button (aligns with sections/video.liquid)
   */
  if (!customElements.get("video-media")) {
    class DtcVideoMedia extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector("video");
        if (!this.video) return;

        if (this.hasAttribute("autoplay")) {
          this.markLoaded();
          this.video.play().catch(() => {});
          return;
        }

        this.addEventListener("click", this.onActivate.bind(this));
        this.video.addEventListener("play", () => this.markLoaded());
      }

      onActivate(event) {
        if (this.hasAttribute("loaded")) return;
        if (event.target.closest("[data-dtc-video-play]")) return;
        this.playFromUser();
      }

      markLoaded() {
        this.setAttribute("loaded", "");
      }

      playFromUser() {
        if (!this.video) return;
        this.markLoaded();
        this.video.controls = true;
        const playBtn = this.closest(".dtc-video--upload")?.querySelector(
          "[data-dtc-video-play]"
        );
        if (playBtn) playBtn.hidden = true;
        return this.video.play();
      }

      play() {
        return this.playFromUser();
      }
    }

    customElements.define("video-media", DtcVideoMedia);
  }

  function getActiveVideoMedia(container) {
    const candidates = container.querySelectorAll("video-media");
    for (const media of candidates) {
      if (media.offsetParent !== null) return media;
    }
    return candidates[0] || null;
  }

  function initDtcVideoUpload(scope) {
    const root = scope || document;
    root.querySelectorAll(".dtc-video--upload").forEach((container) => {
      if (container.dataset.dtcVideoInit === "true") return;
      container.dataset.dtcVideoInit = "true";

      const playBtn = container.querySelector("[data-dtc-video-play]");
      if (!playBtn) return;

      playBtn.addEventListener("click", () => {
        const media = getActiveVideoMedia(container);
        if (!media) return;

        const playPromise =
          typeof media.play === "function" ? media.play() : null;

        if (playPromise && typeof playPromise.then === "function") {
          playPromise.then(() => {
            playBtn.hidden = true;
          }).catch(() => {});
        } else {
          playBtn.hidden = true;
        }
      });
    });
  }

  initDtcProductDescriptions();
  initDtcProductAccordions();
  initDtcVariantSync();
  initDtcSoldOutAmazon();
  initDtcThumbsNav();
  initDtcVideoUpload();

  function getDtcShareData(popup) {
    const url = popup.dataset.shareUrl || window.location.href;
    const text = popup.dataset.shareText || document.title || "Check out this awesome content!";

    return {
      url: encodeURIComponent(url),
      text: encodeURIComponent(text),
    };
  }

  function openDtcSharePopup(popup) {
    if (!popup) return;

    popup.style.display = "block";
    popup.setAttribute("aria-hidden", "false");
  }

  function closeDtcSharePopup(popup) {
    if (!popup) return;

    popup.style.display = "none";
    popup.setAttribute("aria-hidden", "true");
  }

  function bindDtcShareActions(source) {
    if (!source || source.dataset.dtcShareActionsBound === "true") return;

    source.dataset.dtcShareActionsBound = "true";

    source.querySelector("[data-share-facebook], #share-facebook")?.addEventListener("click", (event) => {
      event.preventDefault();
      const { url } = getDtcShareData(source);
      window.open(
        "https://www.facebook.com/sharer/sharer.php?u=" + url,
        "facebook-share-dialog",
        "width=800,height=600"
      );
    });

    source.querySelector("[data-share-twitter], #share-twitter")?.addEventListener("click", (event) => {
      event.preventDefault();
      const { url, text } = getDtcShareData(source);
      window.open(
        "https://twitter.com/intent/tweet?url=" + url + "&text=" + text,
        "twitter-share-dialog",
        "width=800,height=600"
      );
    });

    source.querySelector("[data-share-pinterest], #share-pinterest")?.addEventListener("click", (event) => {
      event.preventDefault();
      const { url, text } = getDtcShareData(source);
      window.open(
        "https://pinterest.com/pin/create/button/?url=" + url + "&description=" + text,
        "pinterest-share-dialog",
        "width=800,height=600"
      );
    });

    source.querySelector("[data-share-email], #share-email")?.addEventListener("click", (event) => {
      event.preventDefault();
      const { url, text } = getDtcShareData(source);
      window.location.href = "mailto:?subject=" + text + "&body=" + url;
    });
  }

  function bindDtcSharePopup(popup) {
    if (!popup || popup.dataset.dtcShareBound === "true") return;

    popup.dataset.dtcShareBound = "true";

    popup.querySelector(".popup_close, [data-dtc-share-close]")?.addEventListener("click", (event) => {
      event.preventDefault();
      closeDtcSharePopup(popup);
    });

    popup.addEventListener("click", (event) => {
      if (event.target === popup) {
        closeDtcSharePopup(popup);
      }
    });

    bindDtcShareActions(popup);
  }

  function syncDtcSharePopupData(popup, trigger) {
    if (!popup || !trigger) return;

    if (trigger.dataset.shareUrl) {
      popup.dataset.shareUrl = trigger.dataset.shareUrl;
    }

    if (trigger.dataset.shareTitle) {
      popup.dataset.shareText = trigger.dataset.shareTitle;
    }
  }

  function resolveDtcSharePopup(trigger) {
    if (!trigger) return null;

    const popupId = trigger.getAttribute("aria-controls");
    if (popupId) {
      return document.getElementById(popupId);
    }

    const scopedPopup = trigger.closest("[data-dtc-share-root]")?.querySelector("[data-dtc-share-popup]");
    if (scopedPopup) return scopedPopup;

    return document.querySelector(".share_popup");
  }

  let dtcShareOpenDelegated = false;

  function initDtcSharePopup(scope) {
    const root = scope || document;

    root.querySelectorAll(".share_popup, [data-dtc-share-popup]").forEach((popup) => {
      bindDtcSharePopup(popup);
    });

    root.querySelectorAll("[data-dtc-share-inline]").forEach((inlineShare) => {
      bindDtcShareActions(inlineShare);
    });

    if (!dtcShareOpenDelegated) {
      dtcShareOpenDelegated = true;

      document.addEventListener("click", (event) => {
        const dtcTrigger = event.target.closest("[data-dtc-share-open]");
        const legacyTrigger = event.target.closest(
          ".instructions-btns-div .btn_parent:last-child .button"
        );
        const trigger = dtcTrigger || legacyTrigger;

        if (!trigger) return;

        event.preventDefault();

        const popup = resolveDtcSharePopup(trigger);
        if (!popup) return;

        syncDtcSharePopupData(popup, trigger);
        bindDtcSharePopup(popup);
        openDtcSharePopup(popup);
      });
    }
  }

  initDtcSharePopup();

  let dtcPrintDelegated = false;

  function resolvePrintCssUrls(trigger) {
    const fromTrigger = trigger?.dataset?.printCss;
    if (fromTrigger) return [fromTrigger];

    const printLink = document.querySelector('link[href*="print.css"]');
    if (printLink?.href) return [printLink.href];

    return [];
  }

  function resolveRecipePrintTargets() {
    const $ = window.jQuery;
    if (!$) return null;

    const dtcTargets = $("dtc-article-ingredients, .dtc-article-cooking-steps");
    if (dtcTargets.length) return dtcTargets;

    return $(".recipe-ingredients-section, .instruction-div");
  }

  function printDtcRecipe(trigger) {
    const $ = window.jQuery;

    if (!$ || typeof $.fn.printThis !== "function") {
      window.print();
      return;
    }

    const targets = resolveRecipePrintTargets();
    if (!targets || !targets.length) {
      window.print();
      return;
    }

    targets.printThis({
      importCSS: true,
      loadCSS: resolvePrintCssUrls(trigger),
    });
  }

  function initDtcRecipePrint() {
    if (dtcPrintDelegated) return;
    dtcPrintDelegated = true;

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest(
        "[data-dtc-print-trigger], [data-print-trigger], #dc_print_btn"
      );
      if (!trigger) return;

      event.preventDefault();
      printDtcRecipe(trigger);
    });
  }

  initDtcRecipePrint();

  /**
   * Prevent FOUC on Shopify Dynamic Checkout buttons.
   * The wrapper starts with .dtc-dynamic-checkout--loading (visibility:hidden).
   * We watch for Shopify's async JS to inject the real button DOM, then reveal it.
   */
  function initDtcDynamicCheckout(scope) {
    const root = scope || document;
    root.querySelectorAll(".dtc-dynamic-checkout--loading").forEach((wrapper) => {
      const paymentBtn = wrapper.querySelector(".shopify-payment-button");
      if (!paymentBtn) {
        wrapper.classList.remove("dtc-dynamic-checkout--loading");
        return;
      }

      // Shopify injects real buttons asynchronously  - reveal once any button appears
      const observer = new MutationObserver(() => {
        if (paymentBtn.querySelector("button, [role='button']")) {
          observer.disconnect();
          wrapper.classList.remove("dtc-dynamic-checkout--loading");
        }
      });
      observer.observe(paymentBtn, { childList: true, subtree: true });

      // Safety fallback: always reveal after 2s regardless
      setTimeout(() => {
        observer.disconnect();
        wrapper.classList.remove("dtc-dynamic-checkout--loading");
      }, 2000);
    });
  }

  initDtcDynamicCheckout();

  if (typeof Shopify !== "undefined" && Shopify.designMode) {
    document.addEventListener("shopify:section:load", (event) => {
      const section = document.getElementById(
        "shopify-section-" + event.detail.sectionId
      );
      if (!section) return;

      section
        .querySelectorAll("[data-dtc-product-description]")
        .forEach((wrap) => {
          delete wrap.dataset.dtcDescriptionInit;
        });

      section.querySelectorAll("[data-dtc-sold-out-actions]").forEach((el) => {
        delete el.dataset.dtcSoldOutAmazonInit;
      });

      section.querySelectorAll("product-gallery-slider").forEach((el) => {
        delete el.dataset.dtcThumbsNavInit;
      });

      section.querySelectorAll("variant-radios").forEach((el) => {
        delete el.dataset.dtcVariantSyncInit;
      });

      section.querySelectorAll("[data-dtc-product-accordions]").forEach((el) => {
        delete el.dataset.dtcAccordionsInit;
      });

      setTimeout(() => {
        initDtcProductDescriptions(section);
        initDtcProductAccordions(section);
        initDtcVariantSync(section);
        initDtcSoldOutAmazon(section);
        initDtcThumbsNav(section);
        initDtcDynamicCheckout(section);
        initDtcVideoUpload(section);
        initDtcSharePopup(section);
      }, 100);
    });
  }
})();
